/**
 * Mida A/B Testing Script
 *
 * This composable conditionally loads the Mida script based on:
 * - Feature flag: NUXT_PUBLIC_MIDA_ENABLED=true (off by default)
 * - Environment: Only loads in production (not homolog/staging/development)
 * - Route: Excludes embedded checkout, widgets, and booking solution (subdomains)
 *
 * The anti-flicker snippet is injected synchronously in <head> so the page is
 * hidden before first paint and the Mida variant can render without flicker
 * (KFT-4099). The `optimize.js` loader runs in experience-first consent mode
 * (`cookie=1`): the variant is shown pre-consent and no cookie/data is stored
 * until consent — consent is granted async via GTM/Iubenda, not here.
 *
 * We are a Nuxt SPA, so `window.isSPA = true` is set immediately before the
 * loader: it tells Mida to watch the History API and retrigger experiments on
 * client-side URL changes (otherwise experiments only run on the initial full
 * page load). See Mida's SPA tracking guide:
 * https://help.mida.so/en/articles/13334089-fixing-tracking-discrepancies-in-single-page-applications-spas
 */

const MIDA_PROJECT_KEY = '7lO2eaD9YWjzGdjmWQvp3Z'
const MIDA_ANTI_FLICKER_TIMEOUT = 2000

// Mida anti-flicker inline script - must be synchronous to hide the page before
// first paint and prevent flicker while the variant is applied.
export const getMidaInlineScript = () => `
var timeout = ${MIDA_ANTI_FLICKER_TIMEOUT};
!function(h,i,d,e){var t,n=h.createElement("style");n.id=e,n.innerHTML='body{position:relative;overflow:hidden}body::after{position:absolute;top:0;bottom:0;left:0;right:0;content:"";background:#fff;z-index:2147483647}',h.head.appendChild(n),t=d,i.rmfk=function(){var t=h.getElementById(e);t&&t.parentNode.removeChild(t)},setTimeout(i.rmfk,t)}(document,window,timeout,"abhide");
`

/**
 * Determines if Mida should be loaded based on current context
 */
export const shouldLoadMida = (
  midaEnabled: boolean,
  appEnv: string | undefined,
  routePath: string,
  isSubdomain: boolean
): boolean => {
  if (!midaEnabled) {
    return false
  }

  const isProduction = appEnv === 'production' || appEnv === 'prod'
  if (!isProduction) {
    return false
  }

  // Don't load on Booking Solution (subdomains)
  if (isSubdomain) {
    return false
  }

  // Don't load on embedded checkout or widgets
  // These patterns work with locale prefixes (e.g., /de-de/embedded/booking)
  const isExcludedRoute =
    routePath.includes('/embedded/') || routePath.includes('/widgets/')
  if (isExcludedRoute) {
    return false
  }

  return true
}

/**
 * Composable to conditionally load the Mida A/B testing script
 *
 * Must be called in app.vue or a layout to ensure it runs on all pages.
 * The anti-flicker snippet loads synchronously in <head> to prevent flicker,
 * ahead of the async `optimize.js` loader.
 */
export const useMidaScript = () => {
  const config = useRuntimeConfig()
  const route = useRoute()
  const isSubdomain = useIsSubdomain()

  const shouldLoad = computed(() =>
    shouldLoadMida(
      config.public.midaEnabled,
      config.public.appEnv,
      route.path,
      isSubdomain.value
    )
  )

  // Use useHead to conditionally inject the Mida scripts
  useHead(() => {
    if (!shouldLoad.value) {
      return {}
    }

    return {
      link: [
        {
          rel: 'preconnect',
          href: 'https://cdn.mida.so',
        },
      ],
      script: [
        // Anti-flicker: synchronous inline, must register before optimize.js loads
        {
          key: 'midaAntiFlicker',
          innerHTML: getMidaInlineScript(),
          type: 'text/javascript',
        },
        // SPA flag: must be set immediately before the loader so Mida watches
        // the History API and retriggers experiments on client-side navigation.
        {
          key: 'midaSpaFlag',
          innerHTML: 'window.isSPA = true;',
          type: 'text/javascript',
        },
        // Mida A/B testing loader (experience-first consent mode via cookie=1)
        {
          key: 'midaOptimize',
          src: `https://cdn.mida.so/js/optimize.js?key=${MIDA_PROJECT_KEY}&cookie=1`,
          type: 'text/javascript',
          async: true,
        },
      ],
    }
  })

  return {
    shouldLoad,
  }
}
